import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchData } from '../services/api';
import '../styles/components/AutoCompleteDropdown.css';

export interface AutoCompleteItem {
  id: number;
  name: string;
  description: string;
}

export interface AutoCompleteResponse<T extends AutoCompleteItem> {
  filters?: any;
  [key: string]: T[] | any; // Dynamic key for items array (e.g., mmt_categories, languages, etc.)
  pagination?: {
    current_page: number;
    total_pages: number;
    total_items: number;
    items_per_page: number;
    order_by: string;
    order_direction: string;
  };
}

interface AutoCompleteDropdownProps<T extends AutoCompleteItem> {
  apiEndpoint: string;
  itemsKey: string; // Key in response object that contains the items array (e.g., 'mmt_categories', 'languages')
  additionalFilters?: Record<string, string | number | boolean>; // Additional query parameters
  value: number;
  onChange: (itemId: number, itemName: string) => void;
  disabled?: boolean;
  error?: string[];
  placeholder?: string;
  emptyMessage?: string; // Message shown when disabled or nothing selected
  className?: string;
}

function AutoCompleteDropdown<T extends AutoCompleteItem>({
  apiEndpoint,
  itemsKey,
  additionalFilters,
  value,
  onChange,
  disabled = false,
  error,
  placeholder = 'Search...',
  emptyMessage,
  className = ''
}: AutoCompleteDropdownProps<T>) {
  const [items, setItems] = useState<T[]>([]);
  const [filteredItems, setFilteredItems] = useState<T[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1); // Single unified index for both mouse and keyboard
  const [navigationMode, setNavigationMode] = useState<'keyboard' | 'mouse'>('mouse');
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  // Load all items on component mount
  useEffect(() => {
    const loadItems = async () => {
      try {
        setIsLoading(true);
        
        // Build query parameters
        const params = new URLSearchParams();
        params.append('all_results', 'true');
        
        if (additionalFilters) {
          Object.entries(additionalFilters).forEach(([key, val]) => {
            if (val !== undefined && val !== null) {
              params.append(key, String(val));
            }
          });
        }
        
        const url = `${apiEndpoint}${apiEndpoint.includes('?') ? '&' : '?'}${params.toString()}`;
        const response = await fetchData<AutoCompleteResponse<T>>(url);
        
        // Extract items using the itemsKey
        const itemsArray = response[itemsKey] as T[] || [];
        setItems(itemsArray);
        setFilteredItems(itemsArray);
      } catch (error) {
        console.error(`Error loading items from ${apiEndpoint}:`, error);
        setItems([]);
        setFilteredItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadItems();
  }, [apiEndpoint, itemsKey, JSON.stringify(additionalFilters)]);

  // Set selected item when value changes
  useEffect(() => {
    if (value !== null && value !== undefined && value !== 0 && items.length > 0) {
      const item = items.find(it => it.id === value);
      if (item) {
        setSelectedItem(item);
        setSearchTerm(item.name);
      } else {
        // Value exists but item not found, clear selection
        console.log('value', value, 'not found in items', items);
        setSelectedItem(null);
        setSearchTerm('');
      }
    } else {
      // Value is 0, null, or undefined - no selection
      console.log('value is 0, null, or undefined or items length is 0 - no selection');
      console.log('value', value);
      console.log('items length', items.length);
      setSelectedItem(null);
      setSearchTerm('');
    }
  }, [value, items]);

  // Filter items based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredItems(items);
    } else {
      const filtered = items.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredItems(filtered);
    }
    // Clear item refs when items change
    itemRefs.current.clear();
  }, [searchTerm, items]);

  // Set initial highlight when dropdown opens with items, or reset when typing
  useEffect(() => {
    if (isOpen && filteredItems.length > 0) {
      // If user is typing (searchTerm has content), reset highlight
      if (searchTerm.trim()) {
        setActiveIndex(-1);
        setNavigationMode('mouse');
      } else {
        // If dropdown just opened (no searchTerm), highlight first item
        setActiveIndex(0);
        setNavigationMode('keyboard');
      }
    } else if (filteredItems.length === 0) {
      // No items available
      setActiveIndex(-1);
      setNavigationMode('mouse');
    }
  }, [isOpen, filteredItems.length, searchTerm]);

  // Scroll highlighted item into view when navigating with keyboard
  useEffect(() => {
    if (navigationMode === 'keyboard' && activeIndex >= 0 && isOpen && menuRef.current) {
      const itemElement = itemRefs.current.get(activeIndex);
      if (itemElement) {
        itemElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }
  }, [activeIndex, navigationMode, isOpen]);

  // Helper function to calculate and update dropdown menu position
  const updateMenuPosition = useCallback(() => {
    if (!inputRef.current) return;
    
    const inputRect = inputRef.current.getBoundingClientRect();
    const optionItem = inputRef.current.closest('.option-item');
    
    if (optionItem) {
      const itemRect = optionItem.getBoundingClientRect();
      setMenuPosition({
        top: inputRect.bottom + window.scrollY,
        left: itemRect.left + window.scrollX,
        width: itemRect.width
      });
    } else {
      setMenuPosition({
        top: inputRect.bottom + window.scrollY,
        left: inputRect.left + window.scrollX,
        width: inputRect.width
      });
    }
  }, []);

  // Update dropdown position on scroll and resize
  useEffect(() => {
    if (!isOpen || !inputRef.current) return;

    // Update position on scroll and resize
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    
    // Initial position update
    updateMenuPosition();

    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  // Helper function to close dropdown (used by Escape, Tab, blur, and click outside)
  const handleCloseDropdown = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
    setNavigationMode('mouse'); // Reset to mouse mode
    // Restore search term to selected item name when closing
    if (selectedItem) {
      setSearchTerm(selectedItem.name);
    }
  }, [selectedItem]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        handleCloseDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleCloseDropdown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    setIsOpen(true);
    setActiveIndex(-1); // Reset highlight when typing
    setNavigationMode('mouse'); // Reset to mouse mode on typing
    // Update position when typing
    updateMenuPosition();
  };

  const handleItemSelect = (item: T) => {
    setSelectedItem(item);
    setSearchTerm(item.name);
    setIsOpen(false);
    setActiveIndex(-1);
    onChange(item.id, item.name);
  };

  const handleInputFocus = () => {
    if (!disabled) {
      // Clear search term to show all options when dropdown opens
      setSearchTerm('');
      setIsOpen(true);
      // Calculate position for dropdown menu
      updateMenuPosition();
      // Note: Initial highlight will be set by useEffect after filteredItems are ready
    }
  };


  // Helper to get item at index
  const getItemAtIndex = useCallback((index: number): T | null => {
    if (index >= 0 && index < filteredItems.length) {
      return filteredItems[index];
    }
    return null;
  }, [filteredItems]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Escape' || e.key === 'Tab') && isOpen) {
      e.preventDefault();
      handleCloseDropdown();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      
      // Calculate max index
      const maxIndex = filteredItems.length - 1;
      
      // Determine starting index: use activeIndex if valid, otherwise start from first
      let currentIndex = activeIndex >= 0 ? activeIndex : 0;
      
      // Switch to keyboard navigation mode
      setNavigationMode('keyboard');
      
      if (e.key === 'ArrowDown') {
        if (!isOpen) {
          setIsOpen(true);
          setActiveIndex(0);
        } else {
          // If activeIndex is -1, start at 0, otherwise move to next item
          if (activeIndex < 0) {
            setActiveIndex(0);
          } else {
            const newIndex = currentIndex < maxIndex ? currentIndex + 1 : currentIndex;
            setActiveIndex(newIndex);
          }
        }
      } else if (e.key === 'ArrowUp') {
        if (isOpen) {
          // If activeIndex is -1, start at last item, otherwise move to previous item
          if (activeIndex < 0) {
            setActiveIndex(maxIndex >= 0 ? maxIndex : 0);
          } else {
            const newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
            setActiveIndex(newIndex);
          }
        }
      }
    } else if (e.key === 'Enter' && isOpen && navigationMode === 'keyboard' && activeIndex >= 0) {
      e.preventDefault();
      const item = getItemAtIndex(activeIndex);
      if (item) {
        handleItemSelect(item);
      }
    }
  };

  const displayValue = disabled && !selectedItem && emptyMessage
    ? emptyMessage
    : searchTerm;

  return (
    <div className={`autocomplete-dropdown ${className}`} ref={dropdownRef}>
      <div className="input-container">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={(e) => {
            // Check if focus is moving to the dropdown menu or its items
            const relatedTarget = e.relatedTarget as Node;
            if (relatedTarget && dropdownRef.current && dropdownRef.current.contains(relatedTarget)) {
              // Focus is moving to dropdown menu, don't close
              return;
            }
            // Check if menu is still open (might have been closed by click or Tab)
            if (isOpen) {
              // Focus is leaving, close the dropdown
              handleCloseDropdown();
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`autocomplete-input ${error ? 'error-input' : ''} ${disabled && !selectedItem && emptyMessage ? 'empty-message' : ''}`}
          disabled={disabled}
          readOnly={disabled && !selectedItem && emptyMessage ? true : false}
        />
        <div 
          className="dropdown-arrow"
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) {
              handleInputFocus();
            }
          }}
          style={{ cursor: disabled ? 'default' : 'pointer' }}
        >
          {isOpen ? '▲' : '▼'}
        </div>
      </div>
      
      {isOpen && !disabled && (
        <div 
          ref={menuRef}
          className={`dropdown-menu ${navigationMode === 'keyboard' ? 'keyboard-mode' : 'mouse-mode'}`}
          style={menuPosition ? {
            position: 'fixed',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            width: `${menuPosition.width}px`,
            zIndex: 9999
          } : undefined}
        >
          {isLoading ? (
            <div className="dropdown-item loading">Loading...</div>
          ) : items.length === 0 ? (
            <div className="dropdown-item no-results">No items found</div>
          ) : filteredItems.length === 0 ? (
            <div className="dropdown-item no-results">No matching items</div>
          ) : (
            filteredItems.map((item, index) => {
              const isSelected = selectedItem?.id === item.id;
              return (
                <div
                  key={item.id}
                  ref={(el) => {
                    if (el) {
                      itemRefs.current.set(index, el);
                    } else {
                      itemRefs.current.delete(index);
                    }
                  }}
                  className={`dropdown-item ${isSelected ? 'selected' : ''} ${navigationMode === 'keyboard' && activeIndex === index ? 'highlighted' : ''}`}
                  onMouseEnter={() => {
                    setNavigationMode('mouse');
                    setActiveIndex(index); // Track hovered item for seamless keyboard switch
                  }}
                  onMouseDown={(e) => {
                    // Prevent input blur from firing before click
                    e.preventDefault();
                    handleItemSelect(item);
                  }}
                >
                  <div className="item-content">
                    <div className="item-name">{item.name}</div>
                    {item.description && (
                      <div className="item-description">{item.description}</div>
                    )}
                  </div>
                  {isSelected && <span className="selected-tick">✓</span>}
                </div>
              );
            })
          )}
        </div>
      )}
      
      {error && (
        <div className="field-error">
          {error.map((errorMsg, index) => (
            <div key={index}>{errorMsg}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AutoCompleteDropdown;

